/**
 * Culians — Bag page (/cart)
 *
 * AJAX quantity +/- and Remove for the full-page bag. Mirrors
 * the drawer's pattern: call /cart/change.js, then re-fetch
 * the section HTML via /cart?sections=culians-cart, then swap
 * the section in place without a full page reload.
 *
 * After every change we also call window.CulianBag.refresh()
 * (defined in culians-bag.js) so the global drawer's count
 * and the header BAG link stay in sync.
 *
 * Event delegation is bound to the document — NOT the section
 * root — so listeners survive the in-place replaceWith() after
 * each refresh. (Earlier version bound to root and the listeners
 * died on the very first refresh.)
 */
(function () {
  'use strict';

  // Must match the section's instance ID in templates/cart.json.
  // Section Rendering API resolves by instance ID for JSON
  // templates, not by file handle. If you rename either of these,
  // both must match or fetches return an empty payload.
  var SECTION_ID = 'culians-cart';

  function getRoot() {
    return document.querySelector('[data-culians-cart]');
  }

  /* ─── Update the header BAG link with the live count ─
   * Updates all <a data-culians-bag-link> elements (added in
   * snippets/header-actions.liquid) to read "Bag (N)" or "Bag".
   * Mirrors culians-bag.js's updateHeaderBagCount() — duplicated
   * here so we can call it directly with the cart count returned
   * by /cart/change.js, without round-tripping through a drawer
   * section fetch.
   */
  function updateHeaderBagCount(itemCount) {
    var links = document.querySelectorAll('[data-culians-bag-link]');
    Array.prototype.forEach.call(links, function (link) {
      var baseText = link.getAttribute('data-bag-base-text');
      if (!baseText) {
        baseText = link.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
        link.setAttribute('data-bag-base-text', baseText);
      }
      link.textContent = itemCount > 0
        ? baseText + ' (' + itemCount + ')'
        : baseText;
    });
  }

  /* ─── Re-render the section after a cart change ──────── */

  function refreshCart() {
    var root = getRoot();
    if (!root) return Promise.resolve();

    return fetch('/cart?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Section fetch failed');
        return response.json();
      })
      .then(function (data) {
        var html = data[SECTION_ID];
        if (!html) {
          // Empty payload — likely a section-ID mismatch in the
          // template. Log loudly so we catch this in dev.
          console.error('Culians cart: empty section payload for', SECTION_ID);
          return;
        }

        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newRoot = doc.querySelector('[data-culians-cart]');
        if (!newRoot) {
          console.error('Culians cart: [data-culians-cart] not found in fetched section');
          return;
        }

        // Decode the re-rendered thumbnails before the swap so rows don't
        // blink on every quantity change (same fix as the drawer).
        return decodeImages(newRoot, 800).then(function () {
          root.replaceWith(newRoot);
          syncAllMax();

          // Keep the global drawer in sync too (count span + items).
          if (window.CulianBag && typeof window.CulianBag.refresh === 'function') {
            window.CulianBag.refresh();
          }
        });
      })
      .catch(function (err) {
        console.error('Culians cart refresh error:', err);
      });
  }

  function decodeImages(root, capMs) {
    if (!root) return Promise.resolve();
    var imgs = Array.prototype.slice.call(root.querySelectorAll('img'));
    if (!imgs.length) return Promise.resolve();
    var work = imgs.map(function (img) {
      img.loading = 'eager';
      if (typeof img.decode !== 'function') return Promise.resolve();
      return img.decode().catch(function () {});
    });
    var cap = new Promise(function (resolve) { setTimeout(resolve, capMs || 800); });
    return Promise.race([Promise.all(work), cap]);
  }

  /* ─── Cart AJAX ──────────────────────────────────────── */

  /* ─── Inline note under the quantity control ─────────
   * Used for stock limits ("Only 1 available.") and for a
   * generic failure. Quiet grey text, fades out on its own.
   */
  var noteTimer = null;
  function showNote(row, text, persistent) {
    if (!row) return;
    var note = row.querySelector('[data-culians-cart-note]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'culians-cart__qty-note';
      note.setAttribute('data-culians-cart-note', '');
      note.setAttribute('role', 'status');
      var qty = row.querySelector('.culians-cart__qty');
      if (qty && qty.parentNode) qty.parentNode.insertBefore(note, qty.nextSibling);
      else row.appendChild(note);
    }
    note.textContent = text;
    note.setAttribute('data-visible', 'true');
    clearTimeout(noteTimer);
    // Stock-limit notes are STATE, not a toast: they stay while the
    // line sits at its ceiling and syncMax() hides them the moment
    // the quantity drops (Suzan, 2026-09-08). Only generic failures
    // fade on their own.
    if (persistent) return;
    noteTimer = setTimeout(function () {
      note.setAttribute('data-visible', 'false');
    }, 4000);
  }

  function hideNote(row) {
    var note = row && row.querySelector('[data-culians-cart-note]');
    if (note) note.setAttribute('data-visible', 'false');
  }

  function availableText(max) {
    return max === 1 ? 'Only 1 available.' : 'Only ' + max + ' available.';
  }

  /* ─── Reflect a stock ceiling on the + button ─────────
   * Rows carry data-max (variant inventory when Shopify tracks
   * it and the policy is "deny"). At the ceiling the + button
   * is shown as inert; clicking it explains instead of failing.
   */
  /* Ceilings Shopify itself reported (422 on /cart/change.js), keyed by
   * line key. Liquid's data-max is the variant's Admin quantity, which can
   * sit above what checkout will actually sell (units committed elsewhere,
   * other locations). Every re-render rebuilds the rows from Liquid, so
   * without this map the server's number was lost on the next refresh and
   * the + came back live. Fix 2026-09-10. */
  var MAX_STORE = 'culians-cart-max';
  var serverMax = (function () {
    try { return JSON.parse(sessionStorage.getItem(MAX_STORE) || '{}') || {}; } catch (e) { return {}; }
  })();

  function rememberMax(lineKey, max) {
    if (!lineKey || isNaN(max)) return;
    serverMax[lineKey] = max;
    try { sessionStorage.setItem(MAX_STORE, JSON.stringify(serverMax)); } catch (e) {}
  }

  function effectiveMax(row) {
    if (!row) return NaN;
    var max = parseInt(row.getAttribute('data-max'), 10);
    var known = serverMax[row.getAttribute('data-line-key')];
    if (typeof known === 'number') max = isNaN(max) ? known : Math.min(max, known);
    return max;
  }

  function syncMax(row) {
    var max = effectiveMax(row);
    var up = row.querySelector('[data-culians-cart-qty-up]');
    var valueEl = row.querySelector('[data-culians-cart-qty-value]');
    if (!up || !valueEl || isNaN(max)) return;
    var current = parseInt(valueEl.textContent, 10) || 0;
    if (current >= max) {
      up.setAttribute('aria-disabled', 'true');
      showNote(row, availableText(max), true);
    } else {
      up.removeAttribute('aria-disabled');
      hideNote(row);
    }
  }

  function syncAllMax() {
    var root = getRoot();
    if (!root) return;
    Array.prototype.forEach.call(root.querySelectorAll('.culians-cart__item'), syncMax);
  }

  function changeLine(lineIndex, quantity, row, previous) {
    // Set the "beat" — line total + grand total dim to 0.35 via
    // CSS while the AJAX is in flight. The new section comes back
    // at full opacity, giving a brief fade-out → snap-in effect.
    // The qty value itself is updated optimistically in the click
    // handler so the number flips instantly; only the values that
    // genuinely have to wait on the server dim during the wait.
    var root = getRoot();
    if (root) root.setAttribute('data-updating', 'true');

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity: quantity })
    })
      .then(function (response) {
        if (response.ok) return response.json();
        // Shopify answers 422 with { status, message, description }
        // when the requested quantity exceeds stock. Surface it
        // instead of leaving the optimistic number on screen.
        return response.json().catch(function () { return {}; }).then(function (err) {
          var e = new Error(err.description || err.message || 'Cart change failed');
          e.status = response.status;
          e.stockLimit = response.status === 422;
          throw e;
        });
      })
      .then(function (cart) {
        // change.js returns the updated cart — use its item_count
        // for the header link immediately so the BAG (N) updates
        // even if the drawer fetch is slow.
        if (cart && typeof cart.item_count === 'number') {
          updateHeaderBagCount(cart.item_count);
        }
        return refreshCart();
      })
      .catch(function (err) {
        console.error('Culians cart change error:', err);
        // Revert the optimistic quantity to what the server holds.
        var valueEl = row && row.querySelector('[data-culians-cart-qty-value]');
        if (valueEl && typeof previous === 'number') valueEl.textContent = String(previous);
        var current = getRoot();
        if (current) current.removeAttribute('data-updating');
        var noteText;
        if (err && err.stockLimit) {
          // Prefer the number Shopify itself reports ("You can only
          // add 3 of ...") over the reference data-max, which can be a
          // few seconds stale if stock moved after the page loaded.
          var max = effectiveMax(row);
          var m = /\b(\d+)\b/.exec(err.message || '');
          if (m) max = parseInt(m[1], 10);
          noteText = isNaN(max) ? (err.message || 'Not enough in stock.') : availableText(max);
          if (row && !isNaN(max)) {
            // Remember it for every later render of this line (and the
            // next page load, via sessionStorage).
            rememberMax(row.getAttribute('data-line-key'), max);
            syncMax(row);
          }
        } else {
          noteText = 'Could not update the bag. Please try again.';
        }
        showNote(row, noteText, !!(err && err.stockLimit));
        // Re-fetch so the page reflects the authoritative cart, then
        // re-attach the note to the freshly rendered row (the old
        // row — and its note — is replaced by the refresh).
        return refreshCart().then(function () {
          var fresh = getRoot();
          var newRow = fresh && fresh.querySelector('[data-line-index="' + lineIndex + '"]');
          showNote(newRow || row, noteText, !!(err && err.stockLimit));
        });
      });
  }

  /* ─── Document-level click delegation ─────────────────
   * Bound once on the document. Survives section replaceWith
   * because we never re-bind on the changing element. The
   * closest('[data-culians-cart]') check scopes us to the page
   * section so a stray data-attribute elsewhere can't trigger
   * a phantom AJAX call.
   */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest(
      '[data-culians-cart-qty-up], [data-culians-cart-qty-down], [data-culians-cart-remove]'
    );
    if (!btn) return;

    // Scope check — only handle clicks inside the bag page section.
    if (!btn.closest('[data-culians-cart]')) return;

    var row = btn.closest('.culians-cart__item');
    if (!row) return;

    var lineIndex = parseInt(row.getAttribute('data-line-index'), 10);
    if (!lineIndex) return;

    // Remove → quantity 0
    if (btn.hasAttribute('data-culians-cart-remove')) {
      e.preventDefault();
      changeLine(lineIndex, 0, row, null);
      return;
    }

    var valueEl = row.querySelector('[data-culians-cart-qty-value]');
    var current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
    if (isNaN(current)) current = 1;

    var next;
    if (btn.hasAttribute('data-culians-cart-qty-up')) {
      e.preventDefault();
      var max = effectiveMax(row);
      if (!isNaN(max) && current >= max) {
        // At the stock ceiling: explain, don't fire a request
        // Shopify will refuse.
        showNote(row, availableText(max), true);
        syncMax(row);
        return;
      }
      next = current + 1;
    } else if (btn.hasAttribute('data-culians-cart-qty-down')) {
      e.preventDefault();
      next = Math.max(0, current - 1);
    } else {
      return;
    }

    // Optimistic UI only for −, which Shopify never refuses. The +
    // waits for the server: Liquid's data-max can sit above what
    // checkout will sell, and an optimistic 11 that snaps back to 10
    // read as a bug (2026-09-10). The data-updating dim is the
    // feedback in the meantime.
    if (valueEl && next > 0 && next < current) {
      valueEl.textContent = String(next);
    }

    changeLine(lineIndex, next, row, current);
  });

  // Initial state: grey out + on any line already at its stock ceiling.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllMax);
  } else {
    syncAllMax();
  }
})();
